// Ship kinematics and the aether-pressure manifold, shared verbatim by the
// server tick and the pilot's client prediction — the same contract the
// skiff established (identical integrator on both sides, tuning derived
// ONLY from replicated band words plus the replicated routing word).
//
// The Squadrons bar, in Bebop's idiom: a ship runs on aether pressure, and
// the pilot ROUTES that pressure between three manifolds in real time —
//   the BURNERS (engines: top speed, and the surge tank that feeds a boost),
//   the COILS   (weapons: how fast the gun coils wind back up), and
//   the VEIL    (shields: how fast the protective veil re-knits).
// Favoring one starves the others; EVEN splits the difference. The skill
// is constant re-routing mid-flight. Combat (the next piece) consumes the
// coil and veil meters; this piece makes them real and replicated so the
// state machine is already load-bearing when guns arrive. Overcharge
// states arrive later as ADDITIONS to PowerFocus — nothing here assumes
// the four words are final.
//
// Pure functions; no renderer, no I/O, no hidden server inputs.

import type { ShipTuning } from "./equipment";
import { SHIP_WEAR_TUNING, shipPerformance } from "./equipment";
import { wrapAngle } from "./movement";
import type { InputMessage } from "./protocol";
import { type AreaPlan, CITY_PAD, SPACEPORT_PAD } from "./world-plan";
import { asWorldId, heightFieldFor, type WorldId } from "./worlds";

/** Ship top speed at even pressure, m/s (surface air). */
export const SHIP_MAX_SPEED = 60;
/** Ship acceleration, m/s². */
export const SHIP_ACCEL = 14;
/** Braking deceleration, m/s². */
export const SHIP_BRAKE = 26;
/** Slow reverse cap for pad maneuvering, m/s. */
export const SHIP_REVERSE_SPEED = 4;
/** Vertical rate at full climb intent, m/s. */
export const SHIP_CLIMB_RATE = 24;
/** How fast vy chases its target, m/s². */
export const SHIP_CLIMB_ACCEL = 30;
/** A dry hull sinks at this rate until it grounds, m/s. */
export const SHIP_DRY_SINK = 6;
/** Yaw rate when crawling, rad/s. */
export const SHIP_YAW_RATE_LOW = 1.6;
/** Yaw rate flat-out, rad/s. */
export const SHIP_YAW_RATE_HIGH = 0.45;
/** Coasting drag when the stick is released, m/s². */
export const SHIP_DRAG = 6;
/** Hover height above ground when landed/skimming, metres. */
export const SHIP_HOVER = 2.4;
/** Board within this many metres of a parked ship. */
export const SHIP_MOUNT_RANGE = 7;
/** Dismount only this close to the ground (above hover), metres. */
export const SHIP_LANDED_TOLERANCE_M = 0.6;
/** ...and only this slow, m/s. */
export const SHIP_LANDED_MAX_SPEED = 2;

/** Climbing past this altitude on the surface commits the ascent. */
export const SURFACE_CEILING_M = 260;
/** Integrator hard ceiling on the surface (handoff fires first). */
export const SURFACE_CLAMP_M = 320;
/** Sinking to this altitude in the Black commits the descent. */
export const SPACE_DESCENT_Y = 8;
/** The Black's hard ceiling, metres above the orbit shell. */
export const SPACE_CEILING_M = 1600;
/** Cruise multiplier in the Black — open space reads fast. */
export const SPACE_SPEED_SCALE = 3;
/** Arrive in the Black at this altitude after an ascent. */
export const SPACE_ARRIVAL_Y = 70;
/** Arrive over the planet at this altitude after a descent. */
export const SURFACE_ARRIVAL_Y = 200;

/**
 * Where each world hangs in the Black. The space room is ONE room whose
 * flight plane carries both planets: the frontier's sky is centred where
 * it always was (moored ships and old poses stay honest), Meridian's sky
 * is a real distance away — crossing between them is minutes of flying,
 * which is the friction pillar rendered literal.
 */
export const SPACE_ANCHORS: Record<WorldId, { x: number; z: number }> = {
  frontier: { x: 0, z: 0 },
  meridian: { x: 9200, z: -9200 },
};

/** Sink to the descent floor within this range of a world's anchor to
 *  commit a descent onto THAT world. Outside every anchor there is nothing
 *  below to land on: a hull that can still climb rides the floor, and one
 *  that cannot falls through it to the nearest world (shipHoldsStation). */
export const DESCENT_COMMIT_RADIUS_M = 3600;

/**
 * How far out over its own anchor an ascent can put you — the size of a
 * world's patch of sky.
 *
 * This exists because a surface is a SQUARE and a world seen from the
 * Black is a DISC. The frontier is 16,384 m across, so its corners reach
 * 11,585 m from its centre, while the two anchors are only 13,010 m
 * apart: mapping the square out one-for-one hung the frontier's
 * south-east corner 1,437 m from Meridian's anchor, deep inside
 * Meridian's descent radius. Climbing off that corner and sinking 62 m
 * landed you on the other planet for under half a percent of the
 * crossing's charge — both halves of the inter-planet friction law
 * (vision pillar 1) bypassed by geometry, over a contiguous 2.6 km of
 * ground, not a knife edge.
 *
 * Inside the descent radius on purpose: an ascent you did not mean to
 * make is undone by sinking back down onto the world you left.
 */
export const ASCENT_SKY_RADIUS_M = 2400;

/** Whose sky is this point in — the nearest anchor's world, at any range. */
export function nearestSpaceWorld(x: number, z: number): WorldId {
  let best: WorldId = "frontier";
  let bestSq = Number.POSITIVE_INFINITY;
  for (const world of Object.keys(SPACE_ANCHORS) as WorldId[]) {
    const anchor = SPACE_ANCHORS[world];
    const dx = x - anchor.x;
    const dz = z - anchor.z;
    const dSq = dx * dx + dz * dz;
    if (dSq < bestSq) {
      best = world;
      bestSq = dSq;
    }
  }
  return best;
}

/** Which world a descent at (x, z) would land on, if any. */
export function descentWorldAt(x: number, z: number): WorldId | undefined {
  const world = nearestSpaceWorld(x, z);
  const anchor = SPACE_ANCHORS[world];
  const dx = x - anchor.x;
  const dz = z - anchor.z;
  return dx * dx + dz * dz <= DESCENT_COMMIT_RADIUS_M * DESCENT_COMMIT_RADIUS_M ? world : undefined;
}

/**
 * Can this hull hold itself off the floor of the Black?
 *
 * The Black has a floor, and what that floor IS depends on what is holding
 * you up. A hull with pressure in it can climb away whenever it likes, so
 * for that one the floor is a floor: ride it, look around, leave when you
 * please. A dry hull has nothing left to climb with, and for that one the
 * floor gives way — out where no world sits under the descent, the nearest
 * one takes her instead (decision 14).
 *
 * Keyed on the climb rather than on the word "dry" so that any future
 * state which takes the climb away inherits the bottom instead of becoming
 * a new place to be stuck.
 */
export function shipHoldsStation(tuning: Pick<ShipTuning, "climbFactor">): boolean {
  return tuning.climbFactor > 0;
}

/**
 * Where a descent puts you: on approach ~600 m out from the world's pad,
 * at altitude, nose already on the pad — you arrive FLYING and fly the
 * landing yourself. Each planet has one port, and the port is where the
 * world is.
 */
export const SURFACE_APPROACH = (() => {
  const x = -380;
  const z = 520;
  return {
    x,
    z,
    y: SURFACE_ARRIVAL_Y,
    yaw: Math.atan2(SPACEPORT_PAD.x - x, SPACEPORT_PAD.z - z),
  };
})();

/** Meridian's approach: over the terraces west of the crown, nose on the
 *  landing apron — you cross the whole skyline on the way in. */
export const CITY_APPROACH = (() => {
  const x = -420;
  const z = -360;
  return {
    x,
    z,
    y: SURFACE_ARRIVAL_Y,
    yaw: Math.atan2(CITY_PAD.x - x, CITY_PAD.z - z),
  };
})();

/** The approach pose a descent onto `world` arrives at. */
export function approachFor(world: WorldId): { x: number; z: number; y: number; yaw: number } {
  return world === "meridian" ? CITY_APPROACH : SURFACE_APPROACH;
}

/**
 * Where an ascent puts you: over your own world's anchor in the Black, at
 * the same local offset you climbed out over, heading preserved — the
 * handoff changes the scene, not your vector.
 *
 * With one correction the square-to-disc mismatch forces: the offset is
 * held to ASCENT_SKY_RADIUS_M, direction intact. Climb off the middle of
 * a world and nothing moves at all; climb off a far corner and you are
 * drawn in to the rim of your own world's sky rather than flung most of
 * the way to the neighbouring planet. That is the honest picture anyway —
 * from up here the world you left is a disc a couple of kilometres wide,
 * not the sixteen-kilometre square you were walking on.
 */
export function spaceArrivalPose(
  from: { x: number; z: number; yaw: number },
  world?: WorldId | string,
): {
  x: number;
  z: number;
  yaw: number;
  y: number;
} {
  const anchor = SPACE_ANCHORS[asWorldId(world)];
  const reach = Math.hypot(from.x, from.z);
  const pull = reach > ASCENT_SKY_RADIUS_M ? ASCENT_SKY_RADIUS_M / reach : 1;
  return {
    x: anchor.x + from.x * pull,
    z: anchor.z + from.z * pull,
    yaw: from.yaw,
    y: SPACE_ARRIVAL_Y,
  };
}

// ---------------------------------------------------------------------------
// The manifold: three meters, one routing word, one boost verb.

/** Where the pressure is routed. "even" is the balanced default. */
export const POWER_FOCI = ["burners", "coils", "veil", "even"] as const;
export type PowerFocus = (typeof POWER_FOCI)[number];

/** Coerce a replicated string to a routing word; unknown reads "even". */
export function asPowerFocus(value: string): PowerFocus {
  return (POWER_FOCI as readonly string[]).includes(value) ? (value as PowerFocus) : "even";
}

/** The three meters, each 0..1. Server-integrated, replicated as floats
 *  so the instrument can bind a needle to real telemetry — rendered only
 *  ever as analog gauge language, never as digits. */
export interface PowerMeters {
  /** The surge tank. Builds only while the burners are favored. */
  boost: number;
  /** Gun-coil pressure. Combat consumes this; it regenerates now. */
  coil: number;
  /** The veil's knit. Combat consumes this; it regenerates now. */
  veil: number;
}

export const POWER = {
  /** Surge tank build rate while burners are favored, per second. */
  boostBuild: 0.13,
  /** Surge tank drain while boosting, per second (~2.2 s of full burn). */
  boostDrain: 0.45,
  /** Coil regen per second: favored / even / starved. */
  coilRate: { favored: 0.24, even: 0.1, starved: 0.03 },
  /** Veil regen per second: favored / even / starved. */
  veilRate: { favored: 0.22, even: 0.09, starved: 0.04 },
  /** Speed/accel factors while burners are favored. */
  burnersSpeed: 1.22,
  burnersAccel: 1.35,
  /** Speed factor while pressure is routed away from the burners. */
  starvedSpeed: 0.86,
  /** Boost multipliers on top of everything else. Dramatic on purpose. */
  boostSpeed: 1.8,
  boostAccel: 2.4,
} as const;

function clamp01(v: number): number {
  return v < 0 ? 0 : v > 1 ? 1 : v;
}

/**
 * Advance the three meters by dt seconds. Pure; both sides may run it
 * (the server's result is authoritative and replicated). `boosting` is
 * the RESOLVED state — input.boost gated on reserve, see boostActive.
 */
export function stepPowerMeters(
  meters: PowerMeters,
  focus: PowerFocus,
  boosting: boolean,
  dt: number,
): PowerMeters {
  let boost = meters.boost;
  if (boosting) {
    boost -= POWER.boostDrain * dt;
  } else if (focus === "burners") {
    boost += POWER.boostBuild * dt;
  }
  const coilRate =
    focus === "coils"
      ? POWER.coilRate.favored
      : focus === "even"
        ? POWER.coilRate.even
        : POWER.coilRate.starved;
  const veilRate =
    focus === "veil"
      ? POWER.veilRate.favored
      : focus === "even"
        ? POWER.veilRate.even
        : POWER.veilRate.starved;
  return {
    boost: clamp01(boost),
    coil: clamp01(meters.coil + coilRate * dt),
    veil: clamp01(meters.veil + veilRate * dt),
  };
}

/** Is the boost live this instant? Held AND there is surge left. */
export function boostActive(input: Pick<InputMessage, "boost">, boostMeter: number): boolean {
  return input.boost === true && boostMeter > 0;
}

/**
 * The tuning a pilot actually flies with: wear/charge bands (words on the
 * wire, exactly the skiff pattern) shaped by the routing word and the
 * resolved boost state. Every input is replicated, so prediction matches.
 */
export function shipFlightTuning(
  wearBand: string,
  chargeBand: string,
  focus: PowerFocus,
  boosting: boolean,
): ShipTuning {
  const base = shipPerformance(wearBand, chargeBand);
  if (base.maxSpeedFactor === 0) return base; // dry: routing moves nothing
  let speed = base.maxSpeedFactor;
  let accel = base.accelFactor;
  if (focus === "burners") {
    speed *= POWER.burnersSpeed;
    accel *= POWER.burnersAccel;
  } else if (focus !== "even") {
    speed *= POWER.starvedSpeed;
  }
  if (boosting) {
    speed *= POWER.boostSpeed;
    accel *= POWER.boostAccel;
  }
  return { ...base, maxSpeedFactor: speed, accelFactor: accel };
}

// ---------------------------------------------------------------------------
// The flight environment: what "down" and "up" mean in this room. Derived
// from the area plan on BOTH sides so prediction and authority agree.

export interface ShipEnv {
  /** Ground elevation under a point (the hover floor sits above it). */
  groundY(x: number, z: number): number;
  /** Integrator hard ceiling, metres. */
  ceilingY: number;
  /** Cruise multiplier (1 in atmosphere, SPACE_SPEED_SCALE in the Black). */
  speedScale: number;
  halfWidth: number;
  halfDepth: number;
}

const FLAT_GROUND = () => 0;

/** The flight environment a plan implies. */
export function shipEnvFor(plan: AreaPlan): ShipEnv {
  if (plan.kind === "space") {
    return {
      groundY: FLAT_GROUND,
      ceilingY: SPACE_CEILING_M,
      speedScale: SPACE_SPEED_SCALE,
      halfWidth: plan.halfWidth,
      halfDepth: plan.halfDepth,
    };
  }
  return {
    // Whichever world this surface stands on supplies its own ground —
    // the hover floor over Meridian's terraces is Meridian's, never the
    // frontier's.
    groundY: heightFieldFor(plan.world),
    ceilingY: SURFACE_CLAMP_M,
    speedScale: 1,
    halfWidth: plan.halfWidth,
    halfDepth: plan.halfDepth,
  };
}

export interface ShipPose {
  x: number;
  /** Altitude, metres — absolute scene y, not height above ground. */
  y: number;
  z: number;
  /** Nose direction, radians (same convention as player yaw). */
  yaw: number;
  /** Signed speed along the nose; negative is reverse. */
  speed: number;
  /** Vertical rate, m/s. */
  vy: number;
}

function approach(value: number, target: number, maxDelta: number): number {
  if (value < target) return Math.min(target, value + maxDelta);
  return Math.max(target, value - maxDelta);
}

function clamp(v: number, min: number, max: number): number {
  return v < min ? min : v > max ? max : v;
}

/**
 * Advance a ship by dt seconds of the pilot's intent. The planar half is
 * the skiff's grammar (chase the stick, steering authority tapers with
 * speed, opposing the nose brakes); the vertical half is a rate-chased
 * climb/sink on input.climb. A dry hull (maxSpeedFactor 0) accepts no
 * throttle AND no climb — it eases down at SHIP_DRY_SINK until it grounds,
 * so running dry at altitude is a landing, never a crash and never a hang.
 *
 * What a dry hull DOES still accept is the rudder: the burners are what
 * died, and a nose is free to point. The coast keeps bleeding out at drag
 * whatever the stick says, so steering a dead hull buys aim, never
 * distance — a pilot who runs dry with speed still on her can spend it
 * pointing somewhere, which is the difference between a glider and a rock.
 * It mints nothing: there is no thrust in a rudder.
 *
 * Ships deliberately ignore plan obstacles: they fly over the world, and
 * the pad they land on is clear ground. (Street furniture tops out around
 * 8 m; a pilot hedge-hopping the settlement can clip a facade visually —
 * an accepted gray-box compromise, logged in ships-and-space.md.)
 *
 * `adrift` is the tripped-manifold state (combat): the stick is dead —
 * no throttle, no climb — but unlike DRY the hull holds its altitude and
 * bleeds speed, because a tripped manifold still floats; only an empty
 * one falls. Replicated, so prediction coasts identically.
 */
export function stepShip(
  pose: ShipPose,
  input: InputMessage,
  dt: number,
  env: ShipEnv,
  tuning: ShipTuning = SHIP_WEAR_TUNING.true,
  adrift = false,
): ShipPose {
  let { moveX, moveZ } = input;
  if (!Number.isFinite(moveX) || !Number.isFinite(moveZ)) {
    moveX = 0;
    moveZ = 0;
  }
  if (adrift) {
    moveX = 0;
    moveZ = 0;
  }
  const dry = tuning.maxSpeedFactor === 0;
  const topSpeed = SHIP_MAX_SPEED * tuning.maxSpeedFactor * env.speedScale;
  const accel = SHIP_ACCEL * tuning.accelFactor;
  const mag = Math.min(1, Math.hypot(moveX, moveZ));
  // The rudder tapers with how fast you are actually going, against the
  // speed this class would fly at with a live cell — so the feel of the
  // stick does not jump the instant the tank runs out.
  const yawReference = dry ? SHIP_MAX_SPEED * env.speedScale : topSpeed;
  let { yaw, speed, vy } = pose;

  if (mag > 0.05) {
    const intentDir = Math.atan2(moveX, moveZ);
    const delta = wrapAngle(intentDir - yaw);
    const opposing = Math.abs(delta) > Math.PI * 0.75;
    if (!opposing) {
      const authority =
        (SHIP_YAW_RATE_LOW +
          (SHIP_YAW_RATE_HIGH - SHIP_YAW_RATE_LOW) *
            Math.min(1, Math.abs(speed) / Math.max(yawReference, 1))) *
        tuning.yawFactor;
      yaw = wrapAngle(yaw + clamp(delta, -authority * dt, authority * dt));
    }
    if (dry) {
      // Rudder only. Holding the stick must never beat drag, or a dead
      // hull would coast forever on a held key.
      speed = approach(speed, 0, SHIP_DRAG * dt);
    } else {
      const throttle = Math.cos(delta) * mag;
      if (throttle >= 0) {
        speed = approach(speed, throttle * topSpeed, accel * dt);
      } else if (speed > 0) {
        speed = approach(speed, 0, SHIP_BRAKE * dt);
      } else {
        speed = approach(speed, throttle * SHIP_REVERSE_SPEED, accel * dt);
      }
    }
  } else {
    speed = approach(speed, 0, SHIP_DRAG * dt);
  }

  // Vertical: chase the climb intent; a dry hull only ever sinks; an
  // adrift hull holds — the trip kills the stick, not the float.
  const climbRaw =
    !adrift && Number.isFinite(input.climb) ? clamp(input.climb as number, -1, 1) : 0;
  const targetVy =
    dry && !adrift ? -SHIP_DRY_SINK : climbRaw * SHIP_CLIMB_RATE * tuning.climbFactor;
  vy = approach(vy, targetVy, SHIP_CLIMB_ACCEL * dt);

  let x = pose.x + Math.sin(yaw) * speed * dt;
  let z = pose.z + Math.cos(yaw) * speed * dt;
  let y = pose.y + vy * dt;

  const maxX = env.halfWidth - SHIP_HOVER;
  const maxZ = env.halfDepth - SHIP_HOVER;
  x = clamp(x, -maxX, maxX);
  z = clamp(z, -maxZ, maxZ);

  const floor = env.groundY(x, z) + SHIP_HOVER;
  if (y <= floor) {
    y = floor;
    if (vy < 0) vy = 0;
  }
  if (y >= env.ceilingY) {
    y = env.ceilingY;
    if (vy > 0) vy = 0;
  }

  return { x, y, z, yaw, speed, vy };
}

/** Is this pose set down (close enough to the ground, slow enough)? */
export function shipLanded(pose: ShipPose, env: ShipEnv): boolean {
  const floor = env.groundY(pose.x, pose.z) + SHIP_HOVER;
  return pose.y <= floor + SHIP_LANDED_TOLERANCE_M && Math.abs(pose.speed) <= SHIP_LANDED_MAX_SPEED;
}

/** Height above the local ground, metres — what the altitude gauge reads. */
export function shipAltitudeAbove(pose: Pick<ShipPose, "x" | "y" | "z">, env: ShipEnv): number {
  return pose.y - env.groundY(pose.x, pose.z);
}
